import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { errorToast, successToast } from "@/app/_module/services/helper";

import type { UpdateAppointment } from "../schema/appointment-schema";
import {
  assignAppointment,
  createAppointment,
  deleteAppointment,
  getAgencyCalendar,
  getAppointmentById,
  getAppointments,
  updateAppointment,
} from "../services/appointment-services";
import type { AgencyCalendarResponse } from "../services/appointment-services";
import appointmentQueryKeys from "../services/keys";
import type { GetAppointmentsParams, GetAppointmentsResponse } from "../types/get-appointment-by-id";

// Hook to get appointments with pagination, search, and status filtering
export const useGetAppointments = (params: GetAppointmentsParams) => {
  return useQuery<GetAppointmentsResponse>({
    queryKey: appointmentQueryKeys.list(params),
    queryFn: () => getAppointments(params),
    staleTime: 0, // Always consider data stale to refetch on mount for instant refresh
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    retry: (failureCount, error) => {
      // Don't retry on validation errors (4xx)
      const status = (() => {
        if (error && typeof error === 'object' && 'response' in (error as object)) {
          const resp = (error as { response?: { status?: unknown } }).response;
          return typeof resp?.status === 'number' ? resp.status : undefined;
        }
        return undefined;
      })();
      if (typeof status === 'number' && status >= 400 && status < 500) {
        return false;
      }
      return failureCount < 3;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

// Hook to get appointments with search and status filtering (optimized for filtering)
export const useGetAppointmentsWithFilters = (
  searchTerm: string,
  statusFilter: string,
  page = 1,
  limit = 50
) => {
  const params: GetAppointmentsParams = {
    page,
    limit,
    query: searchTerm || undefined,
    status: statusFilter !== "all" ? statusFilter : undefined,
  };

  return useQuery<GetAppointmentsResponse>({
    queryKey: appointmentQueryKeys.list(params),
    queryFn: () => getAppointments(params),
    staleTime: 0, // Always fetch fresh data after mutations for real-time updates
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: true, // Refetch when user returns to window
    refetchOnMount: true, // Ensure fresh data on mount
    enabled: true, // Always enabled for filtering
  });
};

// Hook to get single appointment
export const useAppointment = (appointmentId: string) => {
  return useQuery({
    queryKey: appointmentQueryKeys.detail(appointmentId),
    queryFn: () => getAppointmentById(appointmentId),
    enabled: !!appointmentId,
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes
    retry: (failureCount, error) => {
      // Don't retry on 404 errors (appointment not found)
      const status = (() => {
        if (error && typeof error === 'object' && 'response' in (error as object)) {
          const resp = (error as { response?: { status?: unknown } }).response;
          return typeof resp?.status === 'number' ? resp.status : undefined;
        }
        return undefined;
      })();
      if (status === 404) {
        return false;
      }
      return failureCount < 2;
    },
    retryDelay: 1000,
    refetchOnMount: true,
  });
};

// Hook to create appointment
export const useCreateAppointment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createAppointment,
    onMutate: async (newAppointment) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['appointments'] });

      // Snapshot previous values
      const previousAppointments = queryClient.getQueriesData({ queryKey: ['appointments'] });

      // Optimistically add the new appointment to cache
      queryClient.setQueriesData(
        { queryKey: ['appointments'] },
        (old: any) => {
          if (!old) return old;

          const optimisticAppointment = {
            ...newAppointment,
            id: `temp-${Date.now()}`,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          };

          // Handle different response formats
          // Format 1: { data: [...] }
          if (old.data && Array.isArray(old.data)) {
            return {
              ...old,
              data: [optimisticAppointment, ...old.data],
            };
          }

          // Format 2: { items: [...] }
          if (old.items && Array.isArray(old.items)) {
            return {
              ...old,
              items: [optimisticAppointment, ...old.items],
            };
          }

          // Format 3: Direct array [...]
          if (Array.isArray(old)) {
            return [optimisticAppointment, ...old];
          }

          // Unknown format, return as-is
          return old;
        }
      );

      return { previousAppointments };
    },
    onSuccess: async (data, variables) => {
      // Invalidate ALL appointment queries without refetchType restriction
      await queryClient.invalidateQueries({
        predicate: (query) => {
          const queryKey = query.queryKey;
          return Array.isArray(queryKey) && queryKey[0] === 'appointments';
        },
      });

      // Force refetch to get actual data from server
      await queryClient.refetchQueries({
        predicate: (query) => {
          const queryKey = query.queryKey;
          return Array.isArray(queryKey) && queryKey[0] === 'appointments';
        },
      });

      // Also invalidate dashboard queries
      await queryClient.invalidateQueries({
        queryKey: ['dashboard'],
      });

      successToast("Appointment created successfully");
    },
    onError: (error, variables, context) => {
      // Rollback on error
      if (context?.previousAppointments) {
        context.previousAppointments.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      errorToast("Failed to create appointment");
    },
  });
};

// Hook to update appointment
export const useUpdateAppointment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      appointmentId,
      data,
    }: {
      appointmentId: string;
      data: Partial<UpdateAppointment>;
      /** Suppress toasts — for background sweeps like auto-no-show */
      silent?: boolean;
    }) => updateAppointment(appointmentId, data),
    onSuccess: async (data, { appointmentId, silent }) => {
      // Invalidate and force refetch all appointment-related queries immediately
      try {
        // First invalidate all queries
        await queryClient.invalidateQueries({
          predicate: (query) => {
            const queryKey = query.queryKey;
            return Array.isArray(queryKey) &&
              (queryKey[0] === 'appointments' || queryKey[0] === 'dashboard');
          },
        });

        // Force immediate refetch of all active queries
        await queryClient.refetchQueries({
          predicate: (query) => {
            const queryKey = query.queryKey;
            return Array.isArray(queryKey) &&
              (queryKey[0] === 'appointments' || queryKey[0] === 'dashboard');
          },
          type: 'active', // Only refetch currently mounted queries
        });
      } catch (err) {

      }

      if (!silent) successToast("Appointment updated successfully");
    },
    onError: (error, variables, context) => {
      if (!(variables as any)?.silent) errorToast("Failed to update appointment");
    },
  });
};

// Hook to delete appointment
export const useDeleteAppointment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteAppointment,
    onMutate: async (appointmentId) => {
      // Cancel any outgoing refetches to avoid overwriting optimistic update
      await queryClient.cancelQueries({ queryKey: appointmentQueryKeys.lists() });

      // Snapshot the previous value for rollback
      const previousAppointments = queryClient.getQueriesData({ queryKey: appointmentQueryKeys.lists() });

      // Optimistically remove the appointment from all cached queries
      queryClient.setQueriesData({ queryKey: appointmentQueryKeys.lists() }, (old: any) => {
        if (!old?.items) return old;
        return {
          ...old,
          items: old.items.filter((apt: any) => apt.id !== appointmentId),
          meta: {
            ...old.meta,
            totalItems: Math.max((old.meta?.totalItems || 0) - 1, 0)
          }
        };
      });

      return { previousAppointments };
    },
    onError: (_err, _appointmentId, context) => {
      // Rollback on error
      if (context?.previousAppointments) {
        context.previousAppointments.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      errorToast("Failed to delete appointment");
    },
    onSuccess: async () => {
      // Invalidate and force refetch all appointment-related queries immediately
      try {
        // First invalidate all queries
        await queryClient.invalidateQueries({
          predicate: (query) => {
            const queryKey = query.queryKey;
            return Array.isArray(queryKey) &&
              (queryKey[0] === 'appointments' || queryKey[0] === 'dashboard');
          },
        });

        // Force immediate refetch of all active queries
        await queryClient.refetchQueries({
          predicate: (query) => {
            const queryKey = query.queryKey;
            return Array.isArray(queryKey) &&
              (queryKey[0] === 'appointments' || queryKey[0] === 'dashboard');
          },
          type: 'active', // Only refetch currently mounted queries
        });
      } catch (err) {

      }

      successToast("Appointment deleted successfully");
    },
  });
};

// Hook to get appointment statistics (enhanced)
export const useAppointmentStats = () => {
  return useQuery({
    queryKey: appointmentQueryKeys.stats(),
    queryFn: async () => {
      // Import the enhanced stats function
      const { getAppointmentStats } = await import('../services/appointment-services');
      return await getAppointmentStats();
    },
    staleTime: 0, // Always consider data stale to refetch on mount for instant refresh
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: 2, // Retry failed requests
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });
};

// Hook for bulk operations
export const useBulkUpdateAppointments = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      appointmentIds, 
      updateData 
    }: { 
      appointmentIds: string[]; 
      updateData: Partial<UpdateAppointment> 
    }) => {
      const { bulkUpdateAppointments } = await import('../services/appointment-services');
      await bulkUpdateAppointments(appointmentIds, updateData);
    },
    onSuccess: async () => {
      // Invalidate all appointment-related queries
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: appointmentQueryKeys.lists(),
          refetchType: 'active' // Force refetch of active queries
        }),
        queryClient.invalidateQueries({ queryKey: appointmentQueryKeys.stats() }),
      ]);
      successToast("Appointments updated successfully");
    },
    onError: (error) => {

      errorToast("Failed to update appointments");
    },
  });
};

export const useBulkDeleteAppointments = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (appointmentIds: string[]) => {
      const { bulkDeleteAppointments } = await import('../services/appointment-services');
      await bulkDeleteAppointments(appointmentIds);
    },
    onSuccess: async () => {
      // Invalidate all appointment-related queries
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: appointmentQueryKeys.lists(),
          refetchType: 'active' // Force refetch of active queries
        }),
        queryClient.invalidateQueries({ queryKey: appointmentQueryKeys.stats() }),
      ]);
      successToast("Appointments deleted successfully");
    },
    onError: (error) => {

      errorToast("Failed to delete appointments");
    },
  });
};

// ── Agency calendar (owner/admin) ────────────────────────────────────────────

/**
 * Agency-wide appointment feed for the Agency Calendar tab. The backend decides
 * whether the caller is privileged — an agent calling this gets only their own
 * appointments rather than an error, so the tab degrades instead of breaking.
 */
export const useAgencyCalendar = (
  params: { start?: string; end?: string; includeSubAgencies?: boolean },
  opts?: { enabled?: boolean },
) => {
  return useQuery<AgencyCalendarResponse>({
    queryKey: ["appointments", "agency-calendar", params.start, params.end, params.includeSubAgencies],
    queryFn: () => getAgencyCalendar(params),
    enabled: opts?.enabled ?? true,
    staleTime: 30_000,
  });
};

/** Tag an agent on an appointment (null clears it back to unassigned). */
export const useAssignAppointment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ appointmentId, assignedToUserId }: { appointmentId: string; assignedToUserId: string | null }) =>
      assignAppointment(appointmentId, assignedToUserId),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ["appointments", "agency-calendar"] });
      queryClient.invalidateQueries({ queryKey: appointmentQueryKeys.all() });
      successToast(vars.assignedToUserId ? "Appointment assigned" : "Appointment unassigned");
    },
    onError: (err: unknown) => {
      const msg = (err as { message?: string })?.message || "Failed to assign appointment";
      errorToast(msg);
    },
  });
};
